/**
 * useHistory Hook
 * Manages transcription history state and operations
 */

import { useState, useEffect, useCallback } from 'react';
import {
  saveHistoryItem as dbSave,
  getAllHistory as dbGetAll,
  deleteHistoryItem as dbDelete,
  toggleFavorite as dbToggleFavorite,
  updateHistoryItem as dbUpdate,
  searchHistory as dbSearch,
  clearAllHistory as dbClearAll
} from '../services/db.service';

/**
 * Hook for managing transcription history
 * @returns {Object} History state and operations
 */
export function useHistory() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredHistory, setFilteredHistory] = useState([]);

  // Load history on mount
  useEffect(() => {
    loadHistory();
  }, []);

  // Filter history when search query changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredHistory(history);
      return;
    }
    
    const filtered = history.filter(item => {
      const query = searchQuery.toLowerCase();
      return (
        item.fileName?.toLowerCase().includes(query) ||
        item.result?.summary?.toLowerCase().includes(query) ||
        item.result?.transcription?.toLowerCase().includes(query)
      );
    });
    setFilteredHistory(filtered);
  }, [searchQuery, history]);

  /**
   * Load all history items
   */
  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const items = await dbGetAll();
      setHistory(items);
      setFilteredHistory(items);
    } catch (err) {
      console.error('Failed to load history:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Save a new history item
   * @param {Object} data - Data to save
   * @returns {Promise<Object>} Saved item
   */
  const saveItem = useCallback(async (data) => {
    try {
      setError(null);
      const saved = await dbSave(data);
      setHistory(prev => [saved, ...prev]);
      return saved;
    } catch (err) {
      console.error('Failed to save history item:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Delete a history item
   * @param {number} id - Item ID
   */
  const deleteItem = useCallback(async (id) => {
    try {
      setError(null);
      await dbDelete(id);
      setHistory(prev => prev.filter(item => item.id !== id));
    } catch (err) {
      console.error('Failed to delete history item:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Toggle favorite status
   * @param {number} id - Item ID
   */
  const toggleFavorite = useCallback(async (id) => {
    try {
      setError(null);
      const updated = await dbToggleFavorite(id);
      setHistory(prev => prev.map(item => 
        item.id === id ? updated : item
      ));
      return updated;
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Update a history item
   * @param {number} id - Item ID
   * @param {Object} updates - Fields to update
   */
  const updateItem = useCallback(async (id, updates) => {
    try {
      setError(null);
      const updated = await dbUpdate(id, updates);
      setHistory(prev => prev.map(item => 
        item.id === id ? updated : item
      ));
      return updated;
    } catch (err) {
      console.error('Failed to update history item:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Clear all history
   */
  const clearAll = useCallback(async () => {
    try {
      setError(null);
      await dbClearAll();
      setHistory([]);
    } catch (err) {
      console.error('Failed to clear history:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  /**
   * Refresh history from database
   */
  const refresh = useCallback(async () => {
    await loadHistory();
  }, [loadHistory]);

  return {
    // State
    history,
    filteredHistory,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    
    // Operations
    saveItem,
    deleteItem,
    toggleFavorite,
    updateItem,
    clearAll,
    refresh
  };
}
